import React, { useEffect } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import IconButton from './shared/IconButton';
import { formatNumber } from '../utils/numbers';
import { useTranslation } from 'react-i18next';

interface QuantityInputProps {
    value: number;
    onChange: (newValue: number) => void;
    min?: number;
    max?: number;
    step?: number;
    unit?: string;
    size?: 'small' | 'medium' | 'large';
    color?: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error';
    disabled?: boolean;
    width?: number | string;
    decimals?: number;
}

const QuantityInput: React.FC<QuantityInputProps> = ({
    value,
    onChange,
    min = 0,
    max = Infinity,
    step = 1,
    unit = '',
    size = 'small',
    color = 'success',
    disabled = false,
    width = 'auto',
    decimals = 0,
}) => {
    const { t } = useTranslation();
    const [inputValue, setInputValue] = React.useState(value);
    const handleIncrement = () => {
        const newValue = inputValue + step;
        if (newValue <= max) {
            setInputValue(newValue);
            onChange(newValue);
        }
    };

    const handleDecrement = () => {
        const newValue = inputValue - step;
        if (newValue >= min) {
            setInputValue(newValue);
            onChange(newValue);
        }
    };

    const buttonSize = size === 'small' ? 'small' : size === 'medium' ? 'medium' : 'large';
    const fontSize = size === 'small' ? '0.875rem' : size === 'medium' ? '1rem' : '1.25rem';
    const height = size === 'small' ? '25px' : size === 'medium' ? '40px' : '48px';

    useEffect(() => {
        setInputValue(value);
    }, [value]);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                p: '5px',

                opacity: disabled ? 0.5 : 1,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    width,
                    minWidth: 140,
                    height,
                    pointerEvents: disabled ? 'none' : 'auto',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 35 }}>
                    <IconButton iconName="remove" size={buttonSize} onClick={handleDecrement} disabled={disabled || value <= min} color={color} />
                </Box>
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: size === 'small' ? 45 : size === 'medium' ? 50 : 60,
                        borderLeft: '1px solid',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        height: '100%',
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            userSelect: 'none',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {formatNumber(inputValue, t('locale'), decimals)}
                        {unit && (
                            <Typography
                                component="span"
                                sx={{
                                    fontSize: 12,
                                    fontWeight: 400,
                                    ml: 0.5,
                                }}
                            >
                                {unit}
                            </Typography>
                        )}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 35 }}>
                    <IconButton iconName="add" size={buttonSize} onClick={handleIncrement} disabled={disabled || value >= max} color={color} />
                </Box>
            </Box>
        </Box>
    );
};

export default QuantityInput;
